<template>
    <UModal :model-value="open" @update:model-value="$emit('update:open', $event)">
        <UCard :ui="{ rounded: 'rounded-lg', body: { base: 'overflow-visible' } }">
            <template #header>
                <div class="flex items-center justify-between">
                    <h3 class="text-base font-medium leading-6 text-gray-800">Send Report</h3>
                    <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="dismiss()" />
                </div>
            </template>
            <div>
                <template v-if="!subscription">
                    <UFormGroup label="Choose Partner">
                        <UInputMenu
                            v-model="selectedPartner"
                            :loading="searching"
                            :search="searchPartners"
                            placeholder="Select..."
                            size="lg"
                            trailing
                            nullable
                            class="flex-1 mb-6"
                        />
                    </UFormGroup>
                </template>

                <template v-if="reportTypes.length">
                    <UFormGroup label="Type">
                        <USelect
                            size="lg"
                            v-model="selectedReportType"
                            :options="reportTypes"
                            value-attribute="value"
                            placeholder="Choose email type"
                        />
                    </UFormGroup>

                    <template v-if="selectedReportType === EmailReportType.callReport">
                        <UFormGroup v-if="!policyHolder" label="Enter Policy Holder" class="mt-6">
                            <UInput v-model="selectedPolicyHolder" size="lg" />
                        </UFormGroup>
                    </template>

                    <template v-if="selectedReportType === EmailReportType.sickLeave">
                        <UFormGroup
                            v-if="sickLeaveNeedsPolicyHolder"
                            label="Policy Holder"
                            required
                            description="Sick leaves for this partner are routed per policy holder. Only the policy holders configured for this partner can be selected."
                            class="mt-6"
                        >
                            <USelect
                                v-model="selectedSickLeavePolicyHolder"
                                :options="sickLeavePolicyHolderOptions"
                                placeholder="Choose policy holder"
                                size="lg"
                            />
                        </UFormGroup>

                        <UFormGroup label="Doctor's Note" class="mt-6">
                            <UTextarea v-model="doctorNote" size="xl" />
                        </UFormGroup>
                    </template>

                    <UAlert
                        v-if="selectedReportType === EmailReportType.callReport && callReportAlreadyEmailed"
                        title="Call report already emailed"
                        icon="i-ph-check-circle-duotone"
                        color="green"
                        variant="subtle"
                        class="mt-8"
                        description="This call report has already been sent. Pick a different type or close this modal."
                    />

                    <UAlert
                        v-if="selectedReportType === EmailReportType.callReport && callReportBlockedByAllowlist"
                        title="Policy holder not allowed"
                        icon="i-ph-warning-duotone"
                        color="orange"
                        variant="subtle"
                        class="mt-8"
                        description="This customer's policy holder is not in this partner's allowlist. Email cannot be sent."
                    />

                    <UAlert
                        v-if="selectedReportType === EmailReportType.sickLeave && sickLeaveBlocked"
                        :title="sickLeaveBlockedTitle"
                        :description="sickLeaveBlockedDescription"
                        icon="i-ph-warning-duotone"
                        color="orange"
                        variant="subtle"
                        class="mt-8"
                    />

                    <UAlert
                        v-if="selectedReportType && !previewBlocked"
                        title="Email will be sent to:"
                        icon="i-ph-info-duotone"
                        color="primary"
                        variant="subtle"
                        class="mt-8"
                    >
                        <template #description>
                            <ul class="list-disc pl-6 pt-1">
                                <li v-for="email in previewEmailTo" class="text-xs">
                                    {{ email }}
                                </li>
                                <li v-for="email in previewEmailCc" class="text-xs">
                                    {{ email }}
                                    <UBadge color="gray" label="CC" variant="solid" />
                                </li>
                            </ul>
                        </template>
                    </UAlert>
                </template>
            </div>
            <template #footer>
                <div class="flex items-center justify-end space-x-2">
                    <UButton @click="dismiss()" :disabled="confirming" size="lg" color="white"> Cancel </UButton>
                    <UButton @click="confirm()" :disabled="isDisabled" :loading="confirming" size="lg" color="black"> Send </UButton>
                </div>
            </template>
        </UCard>
    </UModal>
</template>

<script setup lang="ts">
import type { CallReport } from '~/types/callReport'
import { EmailReportType, SickLeaveScope, type EmailReportsConfig } from '~/types/partner';
import type { Subscription } from '~/types/subscription';
import { resolveSickLeaveRecipients } from '~/utils/sickLeaveRouting'

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [void]
}>()

const props = defineProps<{
    open: boolean
    report?: CallReport
    subscription?: Subscription
    policyHolder?: string
}>()

const toast = useToast()
const client = useSupabaseClient()

const confirming = ref<boolean>(false)
const searching = ref<boolean>(false)
const selectedPartner = ref<{ value: string; label?: string, email_reports: any }>()
const selectedReportType = ref<EmailReportType>()
const selectedPolicyHolder = ref<string>()
const selectedSickLeavePolicyHolder = ref<string>()
const doctorNote = ref<string>()

const emailReports = computed(() => {
    return (props.subscription?.partner?.metadata?.email_reports ?? selectedPartner.value?.email_reports) as EmailReportsConfig | undefined
})

const callReportBlockedByAllowlist = computed(() => {
    const allowed = emailReports.value?.call_report?.allowed_policy_holders
    if (!allowed?.length) return false
    const ph = props.report?.customer?.policy_holder?.trim()
    return !ph || !allowed.includes(ph)
})

const callReportAlreadyEmailed = computed(() => !!props.report?.report_emailed_at)

const sickLeaveConfig = computed(() => emailReports.value?.sick_leave)

const sickLeavePolicyHolderOptions = computed(() => {
    const config = sickLeaveConfig.value
    if (config?.mode !== SickLeaveScope.perPolicyHolder) return []
    return (config.policy_holders ?? []).map(entry => entry.policy_holder)
})

const sickLeaveNeedsPolicyHolder = computed(() =>
    sickLeaveConfig.value?.mode === SickLeaveScope.perPolicyHolder && !props.policyHolder
)

const sickLeaveResolution = computed(() =>
    resolveSickLeaveRecipients(sickLeaveConfig.value, props.policyHolder ?? selectedSickLeavePolicyHolder.value)
)

const sickLeaveBlocked = computed(() => !sickLeaveResolution.value.ok)

const sickLeaveBlockedTitle = computed(() => {
    if (sickLeaveResolution.value.ok) return ''

    switch (sickLeaveResolution.value.reason) {
        case 'no_policy_holder': return 'No policy holder'
        case 'not_allowed': return sickLeavePolicyHolderOptions.value.length ? 'Policy holder not configured' : 'No policy holders configured'
        case 'duplicate_config': return 'Duplicate policy holder configuration'
        default: return 'Sick leave not configured'
    }
})

const sickLeaveBlockedDescription = computed(() => {
    if (sickLeaveResolution.value.ok) return ''

    switch (sickLeaveResolution.value.reason) {
        case 'no_policy_holder':
            return sickLeaveNeedsPolicyHolder.value
                ? 'This customer has no policy holder recorded. Choose one of this partner\'s configured policy holders above.'
                : 'This customer has no policy holder recorded, so there is no HR department to route to. Email cannot be sent.'
        case 'not_allowed':
            return sickLeavePolicyHolderOptions.value.length
                ? 'This customer\'s policy holder is not configured for this partner. Email cannot be sent.'
                : 'This partner has no policy holders configured for sick leave. Email cannot be sent.'
        case 'duplicate_config':
            return 'This partner has the same policy holder configured more than once, so the recipient is ambiguous. Fix the partner configuration first.'
        default:
            return 'Sick leave email has not been set up for this partner. Email cannot be sent.'
    }
})

const previewBlocked = computed(() => {
    if (selectedReportType.value === EmailReportType.callReport) {
        return callReportBlockedByAllowlist.value || callReportAlreadyEmailed.value
    }
    if (selectedReportType.value === EmailReportType.sickLeave) {
        return sickLeaveBlocked.value
    }
    return false
})

const previewEmailTo = computed(() => {
    if (selectedReportType.value === EmailReportType.sickLeave) {
        return sickLeaveResolution.value.ok ? sickLeaveResolution.value.emailTo : []
    }
    return emailReports.value?.call_report?.email_to.split(',').map(e => e.trim()).filter(Boolean) ?? []
})

const previewEmailCc = computed(() => {
    if (selectedReportType.value === EmailReportType.sickLeave) {
        return sickLeaveResolution.value.ok ? sickLeaveResolution.value.emailCc : []
    }
    return emailReports.value?.call_report?.email_cc?.split(',').map(e => e.trim()).filter(Boolean) ?? []
})

const reportTypes = computed(() => {
    if (!emailReports.value) {
        return []
    }

    return (Object.keys(emailReports.value) || []).sort().map((key) => {
        return { value: key, label: emailReportLabel(key as EmailReportType) ?? key }
    })
})

const isDisabled = computed(() => {
    if (!selectedReportType.value) {
        return true
    }
    if (!props.subscription && !selectedPartner.value) {
        return true
    }
    if (selectedReportType.value === EmailReportType.callReport && !props.policyHolder && !selectedPolicyHolder.value) {
        return true
    }
    if (selectedReportType.value === EmailReportType.callReport && callReportBlockedByAllowlist.value) {
        return true
    }
    if (selectedReportType.value === EmailReportType.callReport && callReportAlreadyEmailed.value) {
        return true
    }
    if (selectedReportType.value === EmailReportType.sickLeave && !doctorNote.value) {
        return true
    }
    if (selectedReportType.value === EmailReportType.sickLeave && sickLeaveBlocked.value) {
        return true
    }
})

watch(() => props.open, (open) => {
    if (open) {
        confirming.value = false
        selectedReportType.value = undefined
        selectedPartner.value = undefined
        selectedPolicyHolder.value = undefined
        selectedSickLeavePolicyHolder.value = undefined
        doctorNote.value = undefined
    }
})

watch(selectedPartner, () => {
    selectedSickLeavePolicyHolder.value = undefined
})

function emailReportLabel(type: EmailReportType) {
    switch (type) {
        case EmailReportType.callReport: return 'Call Report'
        case EmailReportType.sickLeave: return 'Sick Leave'
    }
}

async function confirm() {
    if (props.report?.id) {
        confirming.value = true

        try {
            await $fetch('/api/calls/send-report', {
                method: 'POST',
                body: {
                    id: props.report.id,
                    partnerId: selectedPartner.value?.value,
                    policyHolder: selectedReportType.value === EmailReportType.sickLeave
                        ? selectedSickLeavePolicyHolder.value
                        : selectedPolicyHolder.value,
                    reportType: selectedReportType.value,
                    doctorNote: doctorNote.value,
                }
            })

            toast.add({
                title: 'Report sent successfully!',
                color: 'green',
                icon: 'i-ph-check-circle-duotone'
            })

            emit('reload')
            dismiss()
        } catch (error: any) {
            let title = 'Could not send report, please try again later.'
            if (error.status === 450) title = 'Error: Invalid call report'
            if (error.status === 451) title = 'Error: Invalid customer subscription (no subscription ID)'
            if (error.status === 452) title = 'Error: Invalid customer subscription (not found)'
            if (error.status === 453) title = 'Error: Invalid customer subscription (invalid source)'
            if (error.status === 454) title = 'Partner email has not been set up.'
            if (error.status === 456) title = 'Policy holder not in this partner\'s allowlist.'
            if (error.status === 457) title = 'This policy holder is not configured for sick leave with this partner.'
            if (error.status === 458) title = 'A policy holder is required to send a sick leave for this partner.'

            toast.add({
                title,
                color: 'red',
                icon: 'i-ph-warning-duotone'
            })

            dismiss()
        } finally {
            confirming.value = false
        }
    }
}

function dismiss() {
    emit('update:open', false)
}

async function searchPartners(q: string) {
    searching.value = true

    const { data, error } = await client.from('partners')
        .select('id, name, metadata')
        .is('deleted_at', null)
        .ilike('name', `%${q}%`)
        .or('metadata->email_reports->call_report->email_to.not.is.null,metadata->email_reports->sick_leave->mode.not.is.null')
        .order('name')
        .returns<{ id: string, name: string, metadata: { [key: string]: string } }[]>()

    searching.value = false

    if (data && !error) {
        return data.map((partner) => ({ value: partner.id, label: partner.name, email_reports: partner.metadata?.email_reports }))
    }

    return []
}
</script>
